import { forwardRef } from 'react';
import type { RemixiconComponentType } from '@remixicon/react';
import { Box, Theme, useTheme } from '@mui/material';
import { SxProps } from '@mui/system';
import {
    DesignSystemColorModeFlattened,
    DesignSystemColorScheme,
    DesignSystemRadius,
} from '../../theme/design-system/types';
import { RemixIcon } from '../Icon/RemixIcon';
import {
    DesignSystemFeatureIconSize,
    featureIconBorderRadius,
    featureIconPadding,
} from './FeatureIcon.styleUtils';
import { getRadiusStyles } from '@upheal/design-system/utils/radius';
import { useSlotContext } from '@upheal/design-system/utils/useSlotContext';
import { iconSizingValues } from '@upheal/design-system/theme/design-system/tokens';
import MdiIcon from '../legacy/MdiIcon';
import { FileIcon, FileIconType } from './FileIcon';

/**
 * Visual style of the FeatureIcon container.
 *
 * - `stroke`: Neutral background with border.
 * - `fill-light`: No border and colored background.
 */
export type FeatureIconStyle = 'stroke' | 'fill-light';

export interface FeatureIconBaseProps {
    /**
     * Size of the feature icon container.
     *
     * @default 'md'
     */
    size?: DesignSystemFeatureIconSize;
    /**
     * Border radius of the feature icon container.
     *
     * @default 'rounded'
     */
    radius?: DesignSystemRadius;
    /**
     * Color scheme that determines both background and icon colors.
     *
     * @default 'neutral'
     */
    colorScheme?: DesignSystemColorScheme;
    /**
     * Visual style of the feature icon container.
     * - 'stroke': Neutral background with border.
     * - 'fill-light': No border and colored background.
     *
     * @default 'stroke'
     */
    style?: FeatureIconStyle;
    /**
     * Uses disabled color of the icon.
     */
    disabled?: boolean;
    /**
     * Custom styles.
     */
    sx?: SxProps<Theme>;
    /**
     * Can be used to opt out from usage of the 'featureIcon' slot.
     */
    slot?: null;
}

export interface FeatureIconWithIcon extends FeatureIconBaseProps {
    /**
     * String is a support for legacy MdiIcons for the case we don't have a good Remix icon alternative
     */
    icon: RemixiconComponentType | string;
    fileIcon?: never;
}

export interface FeatureIconWithFileIcon extends FeatureIconBaseProps {
    icon?: never;
    /**
     * Type of file icon to display (e.g., 'pdf').
     */
    fileIcon: FileIconType;
}

export type FeatureIconProps = FeatureIconWithIcon | FeatureIconWithFileIcon;

interface FeatureIconContentProps {
    props: FeatureIconProps;
    size: DesignSystemFeatureIconSize;
    color: DesignSystemColorModeFlattened;
}

const FeatureIconContent = ({
    props,
    size,
    color,
}: FeatureIconContentProps) => {
    if ('fileIcon' in props && props.fileIcon) {
        return <FileIcon type={props.fileIcon} size={iconSizingValues[size]} />;
    }

    const Icon = props.icon;

    if (typeof Icon === 'string') {
        return (
            <Box
                component="span"
                sx={{
                    color,
                    fontSize: iconSizingValues[size],
                    height: iconSizingValues[size],
                }}
            >
                <MdiIcon path={Icon} fontSize="inherit" />
            </Box>
        );
    }

    return <RemixIcon icon={Icon} size={size} color={color} />;
};

export const FeatureIcon = forwardRef<HTMLDivElement, FeatureIconProps>(
    (props, ref) => {
        const theme = useTheme();
        const slot = props.slot === null ? undefined : 'featureIcon';
        const { size = 'md' } = useSlotContext(props, slot);

        const {
            radius = 'rounded',
            colorScheme = 'neutral',
            style = 'stroke',
            sx,
            disabled = false,
        } = props;

        const { borderRadius } = getRadiusStyles({
            componentRadiusValues: featureIconBorderRadius,
            radius,
            size,
            theme,
        });

        const color = (
            colorScheme === 'neutral' && disabled
                ? `ds.fg.${colorScheme}.disabled`
                : `ds.fg.${colorScheme}.primary`
        ) satisfies DesignSystemColorModeFlattened;

        return (
            <Box
                ref={ref}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: featureIconPadding[size],
                    borderRadius,
                    border: style === 'stroke' ? `1px solid` : 'none',
                    borderColor: `ds.border.neutral.primary`,
                    backgroundColor:
                        style === 'fill-light'
                            ? `ds.bg.${colorScheme}.tertiary`
                            : 'ds.bg.neutral.primary',
                    boxShadow: theme.palette.ds.effects.shadow.neutral.xs,
                    ...sx,
                }}
            >
                <FeatureIconContent props={props} size={size} color={color} />
            </Box>
        );
    },
);
